import { useEffect, useState } from "react";
import { Loader2, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";
import { fmt12 } from "@/lib/time";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VIA = { sms: "Text", call: "Phone call", chat: "Console chat" };

const ReportRow = ({ r, reload }) => {
  const [busy, setBusy] = useState(false);
  const run = () => { setBusy(true); apiPost(`/admin/jarvis/reports/${r.report_id}/run`).then((d) => toast.success(`Sent (${d.status})`)).catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => { setBusy(false); reload(); }); };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-400/10 p-3" data-testid={`jarvis-report-${r.report_id}`}>
      <Switch checked={r.enabled} onCheckedChange={(v) => apiPatch(`/admin/jarvis/reports/${r.report_id}`, { enabled: v }).then(reload)} data-testid={`jarvis-report-toggle-${r.report_id}`} />
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm text-white">{r.label}</p>
        <p className="text-xs text-gray-500">
          {r.times.map(fmt12).join(", ")} · {r.days.length === 7 ? "every day" : r.days.map((d) => DAYS[d]).join(" ")} · {VIA[r.channel]}{r.to_name && r.channel !== "chat" ? ` to ${r.to_name}` : ""} · {r.contents.join(", ").replace(/_/g, " ")}
        </p>
        {r.last_sent_at && <p className="text-[11px] text-cyan-300/70" data-testid={`jarvis-report-last-${r.report_id}`}>Last sent {new Date(r.last_sent_at).toLocaleString()} · {r.last_status}</p>}
        {r.instructions && <p className="text-[11px] text-gray-500 italic">"{r.instructions}"</p>}
      </div>
      <button onClick={run} disabled={busy} className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`} data-testid={`jarvis-report-run-${r.report_id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Send now
      </button>
      <button onClick={() => window.confirm(`Delete "${r.label}"?`) && apiDelete(`/admin/jarvis/reports/${r.report_id}`).then(reload)} className="text-gray-500 hover:text-rose-300" data-testid={`jarvis-report-delete-${r.report_id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
};

export const ReportsSection = () => {
  const [items, setItems] = useState(null);
  const load = () => apiGet("/admin/jarvis/reports").then((d) => setItems(d.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <div className={`${HUD.panel} p-5 space-y-4`} data-testid="jarvis-reports-section">
      <div>
        <h3 className="text-white font-['Outfit'] text-lg">Your custom reports</h3>
        <p className="text-xs text-gray-400 mt-1">Just tell JARVIS, e.g. "text me every night at 9 how much we made since we last talked and how many deposits came in" or "call me at noon on weekdays with bookings and unpaid invoices".</p>
      </div>
      {items === null ? <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> :
        items.length ? items.map((r) => <ReportRow key={r.report_id} r={r} reload={load} />) :
        <p className="text-sm text-gray-500" data-testid="jarvis-reports-empty">No custom reports yet.</p>}
    </div>
  );
};
